/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useApolloClient } from '@apollo/client';
import { Button, Container } from '@zoom/components';
import classnames from 'classnames';
import {
  addDays,
  areIntervalsOverlapping,
  endOfDay,
  isAfter,
  isBefore,
  isFuture,
  isPast,
  startOfDay,
  startOfToday,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  ChartOptions,
  Options as HighchartsOptions,
  Point,
  PointDropEventObject,
  PointInteractionEventObject,
  SeriesGanttOptions,
  SeriesOptionsType,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { difference, includes, isEmpty, isNil, pathOr } from 'ramda';
import React, { SyntheticEvent, memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ReactResizeDetector from 'react-resize-detector';
import { useHistory, useLocation, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { AddPersonsSidebar } from './AddPersonsSidebar';
import { DragAndDrop } from './DragAndDrop';
import { ElementsInCollisionModal, ElementsInCollisionModalState } from './ElementsInCollisionModal';
import { useAuth } from '../../../app/AuthorizationProvider';
import { isScheduleJobRunning, useBackgroundTasksStatus } from '../../../app/BackgroundTasksStatusProvider';
import { Alert } from '../../../components/Alert/Alert';
import { FullSizeMessage } from '../../../components/FullSizeMessage';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { PromptModal } from '../../../components/Prompt/PromptModal';
import { TimezoneName } from '../../../components/TimezoneName';
import { Content } from '../../../components/layout/Content';
import { useSidebar } from '../../../components/layout/Sidebar';
import { STATIC_ELEMENT_HEIGHT } from '../../../constants';
import {
  CalculateEstimatedServiceLevelPerQueueDocument,
  CalculateEstimatedServiceLevelPerQueueQuery,
  CalculateEstimatedServiceLevelPerQueueQueryVariables,
  CalculateIntradayWorkContributionDocument,
  CalculateIntradayWorkContributionQuery,
  CalculateWorkContributionDocument,
  CalculateWorkContributionQuery,
  CalculatedWorkContributionData,
  CreateAutoScheduleJobMutationFn,
  CreateScheduleMutationFn,
  CreateScheduleMutationVariables,
  ForecastProduction,
  ForecastType,
  GetAgentsByIdsDocument,
  GetAgentsByIdsQuery,
  GetForecastsIntradayDocument,
  GetForecastsIntradayQuery,
  GetForecastsProductionDocument,
  GetForecastsProductionQuery,
  GetProductionScheduleElementsFromOtherSchedulesDocument,
  GetProductionScheduleElementsFromOtherSchedulesQuery,
  GetQueuesStaffingFromOtherProductionSchedulesDocument,
  GetQueuesStaffingFromOtherProductionSchedulesQuery,
  GetScheduleDocument,
  GetScheduleQuery,
  GetSchedulesDocument,
  GetSchedulesInfoDocument,
  GetSchedulesInfoQuery,
  LockType,
  PutScheduleToProductionMutationFn,
  Queue,
  RemoveScheduleFromProductionMutationFn,
  ScheduleDaySettings,
  ScheduleElement,
  ScheduledAgent,
  Skill,
  StateEnum,
  Timezone,
  UpdateScheduleMutationFn,
} from '../../../graphql/components';
import { ChartData, CreateEditView, CreateEditViewType, IdRouteType, Values } from '../../../types';
import { recordBy } from '../../../utils/array';
import { ScrollPosition, getScrollPosition } from '../../../utils/chart';
import { getUTCHoursOffsetLabel, startOfDayInZone } from '../../../utils/date';
import { convertFieldsEndOfDateRange, createEndOfDateRange, handleEndOfDateRange } from '../../../utils/dateRange';
import { customSort } from '../../../utils/sort';
import { formatTimezoneIdToName } from '../../../utils/timezoneFormatter';
import { Agent } from '../../Agents/types';
import {
  REMOVE_ELEMENT_KEYS,
  defaultDaySettings,
  defaultGetSchedulesVariables,
  getInitialSchedulingState,
  initialSchedulingState,
} from '../config';
import { CONTAINER_TAG, SCHEDULE_JOB_RUNNING_TOAST_ID, SETTINGS_SHOW_ALL_AGENTS } from '../constants';
import {
  AutoScheduleOptions,
  AutoScheduleSelectedRange,
  ChartScheduleElement,
  DaySettings,
  FormValues,
  LocationState,
  PTORequestPartial,
  ProductionChartScheduleElement,
  ProductionScheduleElementsCoverage,
  ScheduleElement as DraggedScheduleElement,
  ScheduleWithoutQueuesStaffing,
  SchedulingAction,
  SchedulingChartType,
  SchedulingGanttChartDragState,
  TransformedProductionForecast,
  TransformedStaffingPerQueue,
} from '../types';
import { handleElementDragDrop } from '../utils/dragUtils';
import { getDaysSettings, getDefaultDaySettings, getScheduledAgents } from '../utils/formatUtils';
import {
  computeElementIdsPerAgents,
  getScheduleElementsForDateRange,
  transformCalculatedQueuesStaffingData,
  transformInitialStaffingPerQueueData,
  transformProductionElementsWorkContributionData,
  transformProductionForecastData,
  transformWorkContributionData,
} from '../utils/mapChartData';
import { getComposerPathname, getPathname } from '../utils/pathname';
import {
  transformProductionScheduleElementsData,
  transformScheduleElementsData,
  transformSourceScheduleElements,
} from '../utils/transformScheduleElementsData';
import { shiftItemsDateRange } from '../utils/transformScheduleItems';
import { CustomXAxis } from './GanttChart/CustomXAxis';
import { GanttChart } from './GanttChart/GanttChart';
import { YLabelList } from './GanttChart/YLabelList';
import { InactiveAgentConfirmationModal, InactiveAgentModalState } from './InactiveAgentConfirmationModal';
import { ScheduleConfigurationSidebar } from './ScheduleConfigurationSidebar';
import { SchedulingChartContainer } from './SchedulingChart/SchedulingChartContainer';
import { SettingsDropdown } from './SettingsDropdown';
import { TimezoneSwitcher } from './TimezoneSwitcher';
import {
  calculateEstimatedServiceLevel,
  calculateProductionElementsWorkContribution,
  calculateWorkContribution,
  changeSourceOfForecast,
  changeTimezone,
  composeData,
  hideNonWorkingAgents,
  loadEstimatedServiceLevel,
  loadEstimatedServiceLevelFailed,
  loadForecastData,
  loadForecastDataFailed,
  loadProductionElementsQueuesStaffing,
  loadProductionElementsQueuesStaffingFailed,
  loadProductionElementsWorkContribution,
  loadProductionElementsWorkContributionFailed,
  loadWorkContribution,
  loadWorkContributionFailed,
  reloadChartData,
  reloadScheduleAfterAutoScheduleFinished,
  removeElements,
  resetDirtyFlag,
  resolveConflictsOnElements,
  restoreForecastData,
  restoreProductionElementsQueuesStaffing,
  restoreProductionScheduleElements,
  selectAgents,
  setAutoScheduleSelectedRange,
  setLoadingProductionScheduleElements,
  setLockedAgentsIds,
  showAllAgents,
  toggleSelectScheduleElementsState,
  updateDaySetting,
  updateElements,
} from './actions';
import {
  OtherHeaderPropsType as HeaderProps,
  getHeaderProps,
  getScrollPositionFromStorage,
  handleMouseUp as handleMouseUpUtil,
  setScrollPositionInStorage,
} from './layoutUtils';
import {
  cropElementsOuterRangeOverflow,
  filterSupervisedAgentIds,
  getAgentElementsWithCorrectPosition,
  hasWorkContributionData,
  reducer,
} from './reducer';

import '../../../styles/scheduling.scss';

const RENDER_TIMEOUT_DELAY = 50;
export interface SchedulingComposerProps {
  agents: Agent[];
  supervisedAgents: Agent[];
  allTimezones: Timezone[];
  createSchedule: CreateScheduleMutationFn;
  createdScheduleId?: string;
  endDateTime: number;
  initialStaffingPerQueue?: TransformedStaffingPerQueue;
  isEdit: boolean;
  isCreate: boolean;
  isLoading: boolean;
  isView: boolean;
  schedule?: ScheduleWithoutQueuesStaffing;
  cloneScheduleId?: string;
  scheduleElements: ScheduleElement[];
  skipRendering: boolean;
  startDateTime: number;
  updateSchedule: UpdateScheduleMutationFn;
  viewType: CreateEditViewType;
  usedScheduleElements?: ChartScheduleElement[];
  scheduledAgents?: ScheduledAgent[];
  scheduleDaysSettings?: ScheduleDaySettings[];
  isDirty: boolean;
  lastViewedTimezone?: string;
  queues: Queue[];
  queueIds: string[];
  skills: Skill[];
  timezone: string;
  putScheduleToProduction: PutScheduleToProductionMutationFn;
  removeScheduleFromProduction: RemoveScheduleFromProductionMutationFn;
  createAutoScheduleJob: CreateAutoScheduleJobMutationFn;
  lastAutoScheduleOptions?: AutoScheduleOptions;
  lastAutoScheduleSelectedRange?: AutoScheduleSelectedRange;
  usePrecalculatedQueuesStaffing: boolean;
}

export interface MouseMoveState {
  clientX: number;
  clientY: number;
}

// component rendered from container having prepared data from API
export const SchedulingComposerComponent: React.FC<SchedulingComposerProps> = ({
  agents,
  supervisedAgents,
  allTimezones,
  createSchedule,
  createdScheduleId,
  endDateTime,
  lastViewedTimezone,
  lastAutoScheduleSelectedRange,
  lastAutoScheduleOptions,
  initialStaffingPerQueue,
  isDirty,
  isEdit,
  isCreate,
  isLoading,
  isView,
  schedule,
  cloneScheduleId,
  queues,
  queueIds,
  scheduleElements,
  skills,
  skipRendering,
  startDateTime,
  updateSchedule,
  putScheduleToProduction,
  removeScheduleFromProduction,
  usedScheduleElements,
  scheduledAgents,
  scheduleDaysSettings,
  timezone,
  viewType,
  createAutoScheduleJob,
  usePrecalculatedQueuesStaffing,
}) => {
  const { t } = useTranslation();
  const { locale } = useAuth();

  const [composerState, dispatch] = useReducer(
    reducer,
    initialSchedulingState,
    // point selection should be disabled for view mode
    getInitialSchedulingState(
      t,
      isDirty,
      timezone,
      lastViewedTimezone,
      lastAutoScheduleSelectedRange,
      lastAutoScheduleOptions,
      startDateTime,
      endDateTime
    )
  );

  const { elementIdsPerAgent, totalPinnableElementIds, inOfficeAgentsPerDayIndex } = useMemo(
    () =>
      computeElementIdsPerAgents(
        composerState.visibleAgentIds,
        composerState.usedScheduleElements,
        composerState.daysSettings
      ),
    [composerState.usedScheduleElements, composerState.visibleAgentIds, composerState.daysSettings]
  );
  const { closeSidebar, isSidebarOpen, openSidebar, toggleSidebar } = useSidebar();

  const [schedulingChartContainerRef, setSchedulingChartContainerRef] = useState<HTMLElement | null>(null);
  const [ganttChartScrollbarContainerRef, setGanttChartScrollbarContainerRef] = useState<HTMLElement | null>(null);
  const [yAxisScrollbarContainerRef, setYAxisScrollbarContainerRef] = useState<HTMLElement | null>(null);
  const [timelineScrollbarContainerRef, setTimelineScrollbarContainerRef] = useState<HTMLElement | null>(null);

  const [ganttTopOffset, setGanttTopOffset] = useState<number | undefined>(undefined);

  const [draggedElement, setDraggedElement] = useState<DraggedScheduleElement | null>(null);
  const [inactiveAgentModal, setInactiveAgentModal] = useState<InactiveAgentModalState>({ open: false });
  const [elementsInCollisionModal, setElementsInCollisionModal] = useState<ElementsInCollisionModalState>({
    state: 'loading',
  });

  const [shouldBlockLeave, setShouldBlockLeave] = useState(composerState.isDirty);
  const [goToFirstStep, setGoToFirstStep] = useState(false);
  const [shouldSwitchToEdit, setShouldSwitchToEdit] = useState(false);
  const [isGanttRendered, setGanttRendered] = useState(false);

  const [cloneMenuOpened, setCloneMenuOpened] = useState(false);
  const [showSeatsAlert, setShowSeatsAlert] = useState(false);

  const [isScheduleConfigurationSidebarOpen, setIsScheduleConfigurationSidebarOpen] = useState(false);

  const ganttChartContainerRef = useRef<HTMLDivElement>(null);
  const ganttChartScrollbarRef = useRef<PerfectScrollbar>(null);
  const schedulingChartScrollbarRef = useRef<PerfectScrollbar>(null);
  const timelineScrollbarRef = useRef<PerfectScrollbar>(null);
  const yAxisScrollbarRef = useRef<PerfectScrollbar>(null);
  const yAxisContainerRef = useRef<HTMLDivElement>(null);
  const ganttWrapperRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const location = useLocation<LocationState>();
  const { id } = useParams<Partial<IdRouteType>>();
  const client = useApolloClient();

  const chartRef = useRef<HighchartsReact>(null);

  const draggedState = useRef<SchedulingGanttChartDragState>({
    shouldAllowDrop: false,
  });

  const mouseMoveStateRef = useRef<MouseMoveState>({
    clientX: 0,
    clientY: 0,
  });

  const [shouldTriggerAutoSchedule, setShouldTriggerAutoSchedule] = useState(false);
  const [lockType, setLockType] = useState(schedule?.info?.lockType ?? LockType.None);

  const {
    autoScheduler: { addNewSchedulesToWatch, schedulesStates, removeScheduleFromWatch },
  } = useBackgroundTasksStatus();

  const activeSchedulesIds = useMemo(
    () => Object.keys(schedulesStates).filter((scheduleId) => isScheduleJobRunning(schedulesStates[scheduleId].state)),
    [schedulesStates]
  );
  const [shouldReloadData, setShouldReloadData] = useState(lockType === LockType.Write);

  const { viewedTimezone } = composerState;

  const startDateInViewedTimezone = startOfDayInZone(new Date(startDateTime), viewedTimezone);
  const startDateInLocalTimezone = utcToZonedTime(startDateInViewedTimezone, viewedTimezone);

  const endDateInLocalTimezone = endOfDay(
    handleEndOfDateRange(utcToZonedTime(new Date(endDateTime).getTime(), viewedTimezone))
  );

  const extendedScheduleRangeEndInLocalTimezone = createEndOfDateRange(endDateInLocalTimezone);
  const extendedScheduleRangeEnd = createEndOfDateRange(endDateTime);
  const scheduleEndsInFuture = isFuture(endDateInLocalTimezone.getTime());

  const allAgentsAreActive =
    !composerState.inactiveAgentIdsWithScheduleElement.length &&
    !composerState.notAssignedAgentIdsWithScheduleElement.length;
  const existAgentWithAssignedShiftTemplate = useMemo(
    () => composerState.selectedAgentIds.some((agentId) => composerState.agents[agentId].shiftTemplateIds?.length),
    [composerState.selectedAgentIds, composerState.agents]
  );

  const pickedTime = (composerState.chartState.xAxis as XAxisOptions[])[0].min as number;
  const pickedTimeLocal = utcToZonedTime(pickedTime, viewedTimezone);
  const endTime = (composerState.chartState.xAxis as XAxisOptions[])[0].max as number;
  const endTimeLocal = utcToZonedTime(endTime, viewedTimezone);
  const chartWidth = (composerState.chartState.chart as ChartOptions).width as number;

  const previousDaysSettings = composerState.daysSettings[composerState.pickedDayIndex - 1];
  let currentDaySettings = composerState.daysSettings[composerState.pickedDayIndex];
  let daySettingsActive = true;

  if (!currentDaySettings) {
    if (composerState.pickedDayIndex === Object.keys(composerState.daysSettings).length && previousDaysSettings) {
      // In case of +1 extra day out of schedule range, previous day settings should be used as inactive
      currentDaySettings = previousDaysSettings;
      daySettingsActive = false;
    } else {
      // Use default empty setting
      currentDaySettings = defaultDaySettings as DaySettings;
    }
  }

  const isMultiQueueSchedule = composerState.queueIds.length > 1;
  const isLoadingForecastOrWorkContribution =
    composerState.isLoadingForecast ||
    composerState.isCalculatingWorkContributionData ||
    composerState.isLoadingProductionScheduleElements ||
    composerState.isLoadingQueuesStaffingOfProductionElements ||
    composerState.isCalculatingWorkContributionOfProductionElements;
  const isProduction = schedule?.info.state === StateEnum.Production;

  /**
   * Due to performance, auto schedule function is currently limited as follow
   *  - Max. number of selected agents is 200
   *  - Max. selected range is 28 days (4 weeks)
   * WFM-2797: This limit was (temporarily) decreased to 100 agents and 14 days (2 weeks)
   */
  const isAutoScheduleLimitExceeded = useMemo(() => {
    if (
      composerState.selectedAgentIds.length > 100 ||
      addDays(composerState.autoScheduleSelectedRange.startDateTime, 14).getTime() <
        composerState.autoScheduleSelectedRange.endDateTime
    ) {
      return true;
    }
    return false;
  }, [composerState.autoScheduleSelectedRange, composerState.selectedAgentIds]);

  const isAutoScheduleRangeOverlapping =
    composerState.autoScheduleSelectedRange.startDateTime > composerState.autoScheduleSelectedRange.endDateTime;

  useEffect(() => {
    const handleMouseMove = debounce((e: MouseEvent) => {
      if (!mouseMoveStateRef.current) {
        return;
      }

      mouseMoveStateRef.current.clientX = e.clientX;
      mouseMoveStateRef.current.clientY = e.clientY;
    }, 50);

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // If schedule is in production and is already in progress, min date range for autoSchedule is from the present day
  const autoScheduleMinDate =
    isProduction && isPast(startDateInLocalTimezone) ? startOfToday() : startDateInLocalTimezone;

  // Checks autoSchedule range startDate value after isProduction prop change and set valid value if needed
  useEffect(() => {
    if (isAfter(autoScheduleMinDate, composerState.autoScheduleSelectedRange.startDateTime)) {
      dispatch(setAutoScheduleSelectedRange('startDateTime', autoScheduleMinDate.getTime()));
    }

    if (isBefore(composerState.autoScheduleSelectedRange.endDateTime, autoScheduleMinDate)) {
      dispatch(setAutoScheduleSelectedRange('endDateTime', startOfDay(endDateInLocalTimezone).getTime()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProduction]);

  useEffect(() => {
    if (id && schedulesStates[id]) {
      const locationState = {
        ...location.state,
        forceRefetchSchedule: true,
      };

      if (isScheduleJobRunning(schedulesStates[id].state)) {
        if (isEdit) {
          setLockType(LockType.Write);
          setShouldReloadData(true);

          history.replace({
            pathname: getComposerPathname(CreateEditView.VIEW, id),
            state: { ...location.state, forceRefetchSchedule: false },
          });
        }
      } else if (isView) {
        setLockType(LockType.None);
        removeScheduleFromWatch(id);

        history.replace({
          pathname: getComposerPathname(CreateEditView.EDIT, id),
          state: locationState,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id && schedulesStates[id], id]);

  useEffect(() => {
    if (lockType === LockType.Write) {
      const toastMessageType =
        id && schedulesStates[id] && isScheduleJobRunning(schedulesStates[id].state)
          ? 'runningInfoWithNotify'
          : 'runningInfo';

      toast.info(t(`schedule.autoSchedule.${toastMessageType}`), {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        toastId: SCHEDULE_JOB_RUNNING_TOAST_ID,
      });
    } else if (toast.isActive(SCHEDULE_JOB_RUNNING_TOAST_ID)) {
      toast.dismiss(SCHEDULE_JOB_RUNNING_TOAST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockType]);

  useEffect(() => {
    if (schedule?.info.lockType) {
      setLockType(schedule.info.lockType);
    }
  }, [schedule?.info.lockType]);

  // Runs on unmount
  useEffect(
    () => () => {
      if (toast.isActive(SCHEDULE_JOB_RUNNING_TOAST_ID)) {
        toast.dismiss(SCHEDULE_JOB_RUNNING_TOAST_ID);
      }
    },
    []
  );

  useEffect(() => {
    if (!isLoading && (!createdScheduleId || shouldReloadData)) {
      if (composerState.selectedAgentIds.length) {
        const updatedElements = transformScheduleElementsData(
          schedule?.scheduleItems ?? [],
          composerState.scheduleElements,
          composerState.daysInScheduleTimezone,
          timezone
        );

        const updateElementsForGantt = getScheduleElementsForDateRange({
          scheduleElements: getAgentElementsWithCorrectPosition(updatedElements, composerState.visibleAgentIds),
          startTimestamp: pickedTime,
          endTimestamp: endTime,
        });

        // overflow to surrounding days is not relevant for determining the scroll position of selected day's range
        const croppedElements = updateElementsForGantt.map(
          cropElementsOuterRangeOverflow(pickedTime, endTime, timezone, composerState.daysInScheduleTimezone)
        );
        const scrollPosition = getScrollPosition(croppedElements, pickedTime);

        if (shouldReloadData) {
          dispatch(
            reloadScheduleAfterAutoScheduleFinished(updatedElements, updateElementsForGantt, initialStaffingPerQueue)
          );
          setScrollPosition(scrollPosition);
          setShouldReloadData(false);
        }
      } else {
        const sourceScheduleElements = transformSourceScheduleElements(scheduleElements, t);
        let elements: ChartScheduleElement[] = [];
        let settings: ScheduleDaySettings[] = getDefaultDaySettings(
          startDateTime,
          convertFieldsEndOfDateRange(endDateTime)
        );

        if (usedScheduleElements && scheduleDaysSettings) {
          elements = usedScheduleElements;
          settings = scheduleDaysSettings;
        } else if (schedule && schedule.scheduleItems && scheduleElements) {
          // transform loaded data
          if (!isCreate) {
            elements = transformScheduleElementsData(
              schedule.scheduleItems,
              sourceScheduleElements,
              composerState.daysInScheduleTimezone,
              timezone
            );
            settings = schedule.daysSettings;
          } else if (cloneScheduleId) {
            const amendedElements = shiftItemsDateRange(schedule.scheduleItems, startDateTime, endDateTime);
            elements = transformScheduleElementsData(
              amendedElements,
              sourceScheduleElements,
              composerState.daysInScheduleTimezone,
              timezone
            );
            settings = getDefaultDaySettings(
              startDateTime,
              convertFieldsEndOfDateRange(endDateTime),
              schedule.scheduledAgents
            );
          }
        }

        prepareInitialDataAndInitCompose(elements, sourceScheduleElements, settings);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    // do not load forecast if queues data hasn't fetched yet
    if (!isLoading && composerState.queueIds.length) {
      dispatch(loadForecastData());
      loadForecast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedTime]);

  useEffect(() => {
    // load forecast when queues data got fetched
    if (composerState.queueIds.length) {
      dispatch(loadForecastData());
      loadForecast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerState.queueIds]);

  const selectedAgentsIds = composerState.selectedAgentIds.toString();

  useEffect(() => {
    const loadProductionScheduleData = async () => {
      await loadProductionScheduleElementsAndRestore();

      /**
       * In case of there was no agent selected in previous state, invoke onResizeHandler to compute ganttTopOffset value
       * which in cascade of other effect should prevent scene from being stuck without rendered chart.
       */
      if (selectedAgentsIds && !ganttTopOffset && ganttChartContainerRef?.current) {
        onResizeHandler();
      }
    };

    if (composerState.schedulingAction !== SchedulingAction.COMPOSE_DATA && composerState.selectedAgentIds.length) {
      loadProductionScheduleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentsIds]);

  useEffect(() => {
    if (!isLoading && isMultiQueueSchedule && composerState.selectedAgentIds.length) {
      loadWorkContributionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentsIds, pickedTime]);

  useEffect(() => {
    document.addEventListener('keydown', handleRemoveElements);

    // Postpone update of scroll to first element during the first load until charts got fully rendered
    if (!isGanttRendered && ganttTopOffset) {
      const elements = composerState.usedScheduleElementsWithinCurrentDay;
      // overflow to surrounding days is not relevant for determining the scroll position of selected day's range
      const croppedElements = elements.map(
        cropElementsOuterRangeOverflow(pickedTime, endTime, timezone, composerState.daysInScheduleTimezone)
      );
      const scrollPosition = getScrollPosition(croppedElements, pickedTime);
      const timeout = setTimeout(() => {
        restoreCurrentScrollYPosition({ scrollTop: scrollPosition.y } as HTMLElement);
        setScrollPosition(scrollPosition);
        setGanttRendered(true);
      }, RENDER_TIMEOUT_DELAY);

      return () => {
        clearTimeout(timeout);
        document.removeEventListener('keydown', handleRemoveElements);
      };
    }

    return () => {
      document.removeEventListener('keydown', handleRemoveElements);
    };
  });

  useEffect(() => {
    const elements = composerState.usedScheduleElementsWithinCurrentDay;
    // overflow to surrounding days is not relevant for determining the scroll position of selected day's range
    const croppedElements = elements.map(
      cropElementsOuterRangeOverflow(pickedTime, endTime, timezone, composerState.daysInScheduleTimezone)
    );
    const scrollPosition = getScrollPosition(croppedElements, pickedTime);

    setScrollPosition(scrollPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedTime, schedulingChartContainerRef, shouldSwitchToEdit]);

  useEffect(() => {
    if (currentDaySettings && currentDaySettings.hideNonWorkingAgents) {
      dispatch(hideNonWorkingAgents());
    } else {
      dispatch(showAllAgents());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentDaySettings?.hideNonWorkingAgents,
    pickedTime,
    composerState.usedScheduleElements,
    composerState.selectedAgentIds,
  ]);

  useEffect(() => {
    setShouldBlockLeave(composerState.isDirty);
  }, [composerState.isDirty]);

  useEffect(() => {
    if (goToFirstStep && shouldBlockLeave) {
      setShouldBlockLeave(false);
    }

    if (goToFirstStep && !shouldBlockLeave) {
      redirectToFirstStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToFirstStep, shouldBlockLeave]);

  useEffect(() => {
    if (shouldSwitchToEdit) {
      setShouldSwitchToEdit(false);
      history.replace({
        pathname: getComposerPathname(CreateEditView.EDIT, id ?? createdScheduleId),
        state: {
          ...location.state,
          scheduleState: {
            /**
             * TODO: location.state.scheduleState could contain outdated state,
             * so we need to derive it from current scene state instead
             */
            ...location.state.scheduleState,
            schedule: {
              ...location.state.scheduleState?.schedule,
              info: {
                ...location.state.scheduleState?.schedule?.info,
                id: id ?? createdScheduleId,
                name: scheduleName,
                state: StateEnum.NonProduction,
              },
            },
            isDirty: false,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSwitchToEdit]);

  useEffect(() => {
    const availableSeats = location?.state?.formValues?.availableSeats;
    if (!availableSeats) {
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const agentIds of Object.values(inOfficeAgentsPerDayIndex)) {
      if (Number(availableSeats) < agentIds.size) {
        setShowSeatsAlert(true);
        return;
      }
    }

    setShowSeatsAlert(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inOfficeAgentsPerDayIndex]);

  useEffect(() => {
    if (shouldTriggerAutoSchedule) {
      setShouldTriggerAutoSchedule(false);

      triggerCreateAutoScheduleJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTriggerAutoSchedule]);

  useEffect(() => {
    if (composerState.schedulingChartType === SchedulingChartType.Intraday) {
      loadIntradayWorkContributionDataOfProductionSchedule();
    } else {
      // we need to calculate queues staffing for both production forecast and SLA
      loadQueuesStaffingOfOtherProductionElements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerState.viewedTimezone, pickedTime]);

  useEffect(() => {
    if (composerState.schedulingAction !== 'COMPOSE_DATA') {
      return;
    }
    /**
     * When opening composer for first time, intraday forecast type cannot be pre-selected,
     * therefore, we need to load queues staffing for original production forecast.
     */
    loadQueuesStaffingOfOtherProductionElements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerState.schedulingAction]);

  const yAxisMax = (composerState.chartState.yAxis as YAxisOptions[])?.[0].max;
  useEffect(() => {
    getOnlyAgentsInViewPort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    yAxisMax,
    pickedTime,
    isGanttRendered,
    composerState.usedScheduleElementsWithinCurrentDay, // this is causing that getOnlyAgentsInViewPort is being executed twice in some cases
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      // when running tests, this handler needs to be initiated manually so that both charts are rendered correctly
      onResizeHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ganttChartContainerRef.current]);

  /**
   * To prepare data, these steps needs to be provided in following order:
   * 1. load not-assigned agents
   * 2. load production schedule elements from other schedules
   * 3. init compose
   * 4. check for possible conflicts between production schedule elements and current schedule's elements
   */
  const prepareInitialDataAndInitCompose = async (
    elements: ChartScheduleElement[],
    sourceScheduleElements: DraggedScheduleElement[],
    settings: ScheduleDaySettings[]
  ) => {
    const retrievedScheduledAgents = scheduledAgents || schedule?.scheduledAgents || [];
    const scheduleAgentsIds = retrievedScheduledAgents.map((agent) => agent.agentId);
    const retrievedAgentsIds = agents.map((agent) => agent.id);
    const missingAgentIds = difference(scheduleAgentsIds, retrievedAgentsIds);
    let missingAgents: Agent[] = [];

    if (missingAgentIds.length) {
      const missingAgentsResult = await client.query<GetAgentsByIdsQuery>({
        query: GetAgentsByIdsDocument,
        variables: { agentIds: missingAgentIds },
        fetchPolicy: 'no-cache',
      });

      missingAgents = missingAgentsResult?.data?.agentsByIds?.agents ?? [];
    }

    const { deletedAgents, notAssignedAgents } = missingAgents.reduce(
      (allMissingAgents, agent) => {
        if (agent.deleted) {
          allMissingAgents.deletedAgents.push(agent);
        } else {
          allMissingAgents.notAssignedAgents.push({ ...agent, isNotAssigned: true } as Agent);
        }
        return allMissingAgents;
      },
      { deletedAgents: [] as Agent[], notAssignedAgents: [] as Agent[] }
    );

    const deletedAgentsName = t('person.unknown');
    const deletedAgentsWithScheduleElement = deletedAgents.map(
      (agent) =>
        ({
          ...agent,
          name: deletedAgentsName,
          surname: '',
        } as Agent)
    );
    const activeAgents: Agent[] = [];
    const inactiveAgents: Agent[] = [];

    agents.forEach((agent) => {
      if (agent.active) {
        activeAgents.push(agent);
      } else {
        inactiveAgents.push(agent);
      }
    });

    const inactiveAgentIdsWithScheduleElement = inactiveAgents
      .filter((ag) => elements.find((el) => el.agentId === ag.id))
      .map(({ id: agentId }) => agentId);

    const lockedAgentsIdsObj: Record<string, boolean> = {};

    if (schedule && isProduction && isEdit) {
      // eslint-disable-next-line no-return-assign
      schedule.scheduledAgents?.forEach((scheduleAgent) => (lockedAgentsIdsObj[scheduleAgent.agentId] = true));
    }

    const preselectedAgents = scheduledAgents ?? schedule?.scheduledAgents ?? [];
    const agentsRecordObj: Record<string, Agent> = { ...composerState.agents };
    const activeAgentsIds = activeAgents.map((agent) => agent.id);

    [...agents, ...deletedAgentsWithScheduleElement, ...notAssignedAgents].forEach((agent) => {
      agentsRecordObj[agent.id] = agent;
    });

    /**
     * Pre-load all available active agents into composer when creating new schedule and no agents have been selected
     * on previous visit of scheduling composer
     */
    const shouldPreloadAllAgents = isCreate && activeAgents.length && !preselectedAgents.length;
    const selectedAgentIds = shouldPreloadAllAgents
      ? activeAgentsIds
      : [...preselectedAgents]
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map(({ agentId }) => agentsRecordObj[agentId])
          .filter((agent) => agent)
          .map(({ id: agentId }) => agentId);

    const allAgentIdsSorted = customSort(
      agentsRecordObj,
      composerState.filter.sortByFields,
      composerState.filter.sortOrder,
      selectedAgentIds
    );

    const allAgentsForLoadOfProductionElements = shouldPreloadAllAgents
      ? allAgentIdsSorted
      : customSort(
          agentsRecordObj,
          composerState.filter.sortByFields,
          composerState.filter.sortOrder,
          selectedAgentIds
        );

    let missingQueuesData: Record<string, Queue> = {};
    let productionScheduleElements: ProductionChartScheduleElement[] = [];
    let productionScheduleElementsCoverage: ProductionScheduleElementsCoverage[] = [];
    let productionScheduleElementsMissingQueueIds: string[] = [];

    if (allAgentsForLoadOfProductionElements.length) {
      // Load production schedule elements only when some agents are preloaded or preselected
      ({
        missingQueuesData,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        productionScheduleElementsMissingQueueIds,
      } = await loadProductionScheduleElements(allAgentsForLoadOfProductionElements, sourceScheduleElements));
    }

    const supervisedAgentsRecordObj = recordBy(supervisedAgents, 'id');

    dispatch(
      composeData({
        activeAgents,
        supervisedAgentsRecordObj,
        agentsRecordObj,
        allAgentIdsSorted,
        deletedAgentsWithScheduleElement,
        missingQueuesData,
        productionScheduleElementsMissingQueueIds,
        queues,
        queueIds,
        skills,
        inactiveAgentIdsWithScheduleElement,
        initialStaffingPerQueue,
        notAssignedAgents,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        selectedAgentIds,
        usePrecalculatedQueuesStaffing,
        includeNotAssignedAgents: !isCreate,
        scheduleElements: sourceScheduleElements,
        usedScheduleElements: elements,
        scheduledAgents: preselectedAgents,
        daysSettings: settings,
        lockedAgentsIds: lockedAgentsIdsObj,
        shouldPreloadAllAgents: isCreate && !preselectedAgents.length && !!agents.length,
      })
    );

    // Check for possible conflicts
    checkForPossibleConflictsWithProductionSchedule(productionScheduleElements, elements);
  };

  const checkForPossibleConflictsWithProductionSchedule = async (
    productionScheduleElements = composerState.productionScheduleElements,
    elements = composerState.usedScheduleElements
  ) => {
    try {
      const scheduledElementsPerAgent = elements.reduce(
        (reducedElements, element) => ({
          ...reducedElements,
          [element.agentId]: [...(reducedElements[element.agentId] ?? []), element],
        }),
        {} as Record<string, ChartScheduleElement[]>
      );

      const productionElementsInConflict: ProductionChartScheduleElement[] = [];
      const sourceSchedulesIdsInConflict = new Set<string>();
      const ptoRequestsInConflictIds = [] as string[];
      const ptoRequestsInConflictRecordObj: Record<string, boolean> = {};
      const productionElementsPerPTORequestIdRecordObj: Record<string, ProductionChartScheduleElement[]> = {};

      productionScheduleElements.forEach((productionElement) => {
        if (!scheduledElementsPerAgent[productionElement.agentId]) {
          return;
        }

        if (productionElement.ptoRequestId) {
          if (!productionElementsPerPTORequestIdRecordObj[productionElement.ptoRequestId]) {
            productionElementsPerPTORequestIdRecordObj[productionElement.ptoRequestId] = [];
          }

          productionElementsPerPTORequestIdRecordObj[productionElement.ptoRequestId].push(productionElement);
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const scheduleElement of scheduledElementsPerAgent[productionElement.agentId]) {
          if (
            areIntervalsOverlapping(
              { start: productionElement.start, end: productionElement.end },
              { start: scheduleElement.start, end: scheduleElement.end }
            )
          ) {
            productionElementsInConflict.push(productionElement);
            if (productionElement.sourceScheduleId) {
              sourceSchedulesIdsInConflict.add(productionElement.sourceScheduleId);
            } else if (
              productionElement.ptoRequestId &&
              !ptoRequestsInConflictRecordObj[productionElement.ptoRequestId]
            ) {
              ptoRequestsInConflictIds.push(productionElement.ptoRequestId);
              ptoRequestsInConflictRecordObj[productionElement.ptoRequestId] = true;
            }
            break;
          }
        }
      });

      // the maximum range of PTO request intersecting with schedule range should be used, not only range which includes colliding schedule items within
      const ptoRequestsInConflict = ptoRequestsInConflictIds.reduce((ptoRequests, requestId) => {
        if (!productionElementsPerPTORequestIdRecordObj[requestId]?.length) {
          return ptoRequests;
        }

        return [
          ...ptoRequests,
          {
            agentId: productionElementsPerPTORequestIdRecordObj[requestId][0].agentId,
            id: requestId,
            endDateTime: Math.max(...productionElementsPerPTORequestIdRecordObj[requestId].map(({ end }) => end)),
            startDateTime: Math.min(...productionElementsPerPTORequestIdRecordObj[requestId].map(({ start }) => start)),
          },
        ];
      }, [] as PTORequestPartial[]);

      if (productionElementsInConflict.length) {
        let schedulesInConflict;

        if (sourceSchedulesIdsInConflict.size) {
          schedulesInConflict = await client.query<GetSchedulesInfoQuery>({
            query: GetSchedulesInfoDocument,
            variables: {
              scheduleIds: Array.from(sourceSchedulesIdsInConflict).toString(),
            },
          });
        }

        if (schedulesInConflict?.data?.schedulesInfo || ptoRequestsInConflictIds.length) {
          setElementsInCollisionModal({
            state: 'open',
            productionElementsInConflict,
            ptoRequestsInConflict,
            productionSchedulesInConflict: schedulesInConflict?.data?.schedulesInfo ?? [],
          });
        }
      } else {
        setElementsInCollisionModal({ state: 'resolved' });
      }
    } catch (e) {
      // error message provided by response provider
    }
  };

  const loadWorkContributionData = async () => {
    if (!isMultiQueueSchedule) {
      return;
    }

    dispatch(loadWorkContribution());

    const queryVariables = {
      parameters: {
        timezone: viewedTimezone,
        agentIds: composerState.selectedAgentIds,
        dateTimeRange: {
          endDateTime: new Date(endTime).toISOString(),
          startDateTime: new Date(pickedTime).toISOString(),
        },
        queueIds: composerState.queueIds,
      },
    };

    try {
      let data: CalculatedWorkContributionData[] = [];

      if (composerState.schedulingChartType === SchedulingChartType.Intraday) {
        const result = await client.query<CalculateIntradayWorkContributionQuery>({
          query: CalculateIntradayWorkContributionDocument,
          variables: queryVariables,
          fetchPolicy: 'no-cache',
        });

        data = result.data.calculatedIntradayWorkContribution || [];
      } else {
        // we need to calculate queues staffing for both production forecast and SLA
        const result = await client.query<CalculateWorkContributionQuery>({
          query: CalculateWorkContributionDocument,
          variables: queryVariables,
          fetchPolicy: 'no-cache',
        });

        data = result.data.calculatedWorkContribution || [];
      }

      const transformedData = transformWorkContributionData(data);

      dispatch(calculateWorkContribution(transformedData));
    } catch (error) {
      // error message provided by response provider
      const errorCode = pathOr(null, ['networkError', 'result', 'errorCode'], error);
      if (
        composerState.schedulingChartType === SchedulingChartType.Intraday &&
        errorCode === 'fe.sch.wc.calculation.missingIntradayForecast'
      ) {
        // switch view back to production forecast as some intraday forecast (for at least 1 of voice queues) is missing and therefore work contributions were not calculated correctly
        dispatch(changeSourceOfForecast(SchedulingChartType.Production, ForecastType.Intraday));
      }

      dispatch(loadWorkContributionFailed());
    }
  };

  const checkIfProductionForecastIsPresent = (forecast: TransformedProductionForecast | null) => {
    if (composerState.schedulingChartType === SchedulingChartType.SLA) {
      if (!isMultiQueueSchedule && (!forecast || Object.values(forecast[queueIds[0]] ?? {}).length === 0)) {
        toast.warning(t('schedule.unableToCalculateEstimatedServiceLevel'));
      }

      if (
        isMultiQueueSchedule &&
        (!forecast ||
          Object.keys(forecast).filter(
            (queueId) =>
              !forecast[queueId] || // no forecast found for queueId
              Object.values(forecast[queueId]).length === 0 || // no forecast data found for queueId
              Object.values(forecast[queueId]).filter((valuePerTimestamp) => valuePerTimestamp === -1).length ===
                Object.values(forecast[queueId]).length // all forecast data contain -1 (eq. originally null values) which means there is no forecast data present for queueId
          ).length === Object.keys(forecast).length)
      ) {
        toast.warning(t('schedule.unableToCalculateEstimatedServiceLevelForAllQueues'));
      }
    }
  };

  const loadForecast = async (
    productionElementsMissingQueueIds = composerState.productionScheduleElementsMissingQueueIds
  ) => {
    /**
     * Forecast of productionElementsMissingQueueIds is no longer used for work contribution calculations related to production forecast,
     * however, we still need them for work contribution calculations related to intraday forecast...
     */
    const queueIdsToLoad =
      composerState.schedulingChartType === SchedulingChartType.Intraday
        ? [...composerState.queueIds, ...productionElementsMissingQueueIds]
        : composerState.queueIds; // covers both production forecast and SLA
    const queryVariables = {
      queueIds: queueIdsToLoad.toString(),
      startDateTime: new Date(pickedTime).toISOString(),
      endDateTime: new Date(endTime).toISOString(),
    };

    try {
      let data: ForecastProduction[] = [];

      if (composerState.schedulingChartType === SchedulingChartType.Intraday) {
        const result = await client.query<GetForecastsIntradayQuery>({
          query: GetForecastsIntradayDocument,
          variables: queryVariables,
          fetchPolicy: 'network-only',
        });

        data = result.data.forecastsIntraday || [];
      } else {
        const result = await client.query<GetForecastsProductionQuery>({
          query: GetForecastsProductionDocument,
          variables: queryVariables,
          fetchPolicy: 'network-only',
        });

        data = result.data.forecastsProduction || [];
      }

      const { transformedData, missingOrIncompleteForecastQueueIds, missingForecastIntervalsPerQueue } =
        transformProductionForecastData(
          data,
          composerState.queues,
          composerState.scheduleStartTime,
          composerState.scheduleEndTime
        );

      checkIfProductionForecastIsPresent(transformedData);

      dispatch(
        restoreForecastData(transformedData, missingOrIncompleteForecastQueueIds, missingForecastIntervalsPerQueue)
      );
    } catch (e) {
      // error message provided by response provider
      checkIfProductionForecastIsPresent(null);
      dispatch(loadForecastDataFailed());
    }
  };

  const loadProductionScheduleElementsAndRestore = async () => {
    if (!composerState.selectedAgentIdsSorted.length) {
      // Do not load production schedule elements when no agents are selected
      return [];
    }

    dispatch(setLoadingProductionScheduleElements(true));
    try {
      const {
        missingQueuesData,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        productionScheduleElementsMissingQueueIds,
      } = await loadProductionScheduleElements(composerState.selectedAgentIdsSorted, composerState.scheduleElements);

      dispatch(
        restoreProductionScheduleElements(
          productionScheduleElements,
          productionScheduleElementsCoverage,
          productionScheduleElementsMissingQueueIds,
          missingQueuesData
        )
      );

      if (productionScheduleElementsMissingQueueIds.length) {
        dispatch(loadForecastData());
        loadForecast(productionScheduleElementsMissingQueueIds);
      }

      return productionScheduleElements;
    } catch (e) {
      dispatch(setLoadingProductionScheduleElements(false));
      return [];
    }
  };

  const loadProductionScheduleElements = async (
    agentIds: string[],
    sourceScheduleElements: DraggedScheduleElement[]
  ) => {
    try {
      const result = await client.query<GetProductionScheduleElementsFromOtherSchedulesQuery>({
        query: GetProductionScheduleElementsFromOtherSchedulesDocument,
        variables: {
          parameters: {
            agentIds,
            dateTimeRange: {
              startDateTime: new Date(startDateTime).toISOString(),
              endDateTime: extendedScheduleRangeEnd.toISOString(),
            },
            scheduleToExclude: id,
          },
        },
        fetchPolicy: 'network-only',
      });

      const allProductionQueuesData = result.data?.productionScheduleElementsFromOtherSchedules.queuesDetails;
      const allProductionQueuesRecord = recordBy(allProductionQueuesData, 'queueId');
      const productionElements = result.data?.productionScheduleElementsFromOtherSchedules.scheduleItems ?? [];
      const queueIntervals = result.data?.productionScheduleElementsFromOtherSchedules.queueIntervals ?? [];

      /**
       * TODO: coverages and original queueIds from other prod schedule are longer needed to be stored within our state,
       * as we do not recalculate staffing for these elements manually but we load precalculated value from BE,
       * so this should be cleaned up
       */
      const productionScheduleElementsCoverage = queueIntervals.map((intervalData) => ({
        agentIds: intervalData.intervalAgentIds,
        queueIds: intervalData.intervalQueueIds,
        scheduleId: intervalData.sourceScheduleId,
        startDateTime: new Date(intervalData.dateTimeRange.startDateTime).getTime(),
        endDateTime: new Date(intervalData.dateTimeRange.endDateTime).getTime(),
      }));

      // Array consisting of unique queue ids
      const allProductionQueueIds = Array.from(
        new Set(productionScheduleElementsCoverage.flatMap((coverageRange) => coverageRange.queueIds))
      );
      const missingQueueIds = difference(allProductionQueueIds, queueIds);
      const missingQueuesData: Record<string, Queue> = {};

      missingQueueIds.forEach((queueId) => {
        missingQueuesData[queueId] = {
          id: allProductionQueuesRecord[queueId].queueId,
          channelType: allProductionQueuesRecord[queueId].channelType,
        } as Queue;
      });

      const transformedProductionElements = transformProductionScheduleElementsData(
        productionElements,
        sourceScheduleElements,
        composerState.days,
        timezone
      );

      return {
        missingQueuesData,
        productionScheduleElementsCoverage,
        productionScheduleElements: transformedProductionElements,
        productionScheduleElementsMissingQueueIds: missingQueueIds,
      };
    } catch (e) {
      // error message provided by response provider
      return {
        missingQueuesData: {},
        productionScheduleElementsCoverage: [],
        productionScheduleElements: [],
        productionScheduleElementsMissingQueueIds: [],
      };
    }
  };

  const loadQueuesStaffingOfOtherProductionElements = async () => {
    if (!composerState.queueIds?.length) {
      // prevent executing before COMPOSE_DATA action was handled
      return;
    }

    if (!isEmpty(composerState.productionElementsWorkContributionData)) {
      // We should release previous contribution data from state
      dispatch(restoreProductionElementsQueuesStaffing({}));
      return;
    }

    dispatch(loadProductionElementsQueuesStaffing());

    try {
      const result = await client.query<GetQueuesStaffingFromOtherProductionSchedulesQuery>({
        query: GetQueuesStaffingFromOtherProductionSchedulesDocument,
        variables: {
          parameters: {
            endDateTime: new Date(endTime).toISOString(),
            scheduleToExclude: id,
            queueIds: queueIds.toString(),
            startDateTime: new Date(pickedTime).toISOString(),
          },
        },
        fetchPolicy: 'no-cache',
      });

      const transformedData = transformInitialStaffingPerQueueData(
        result.data.queuesStaffingFromOtherProductionSchedules
      );

      dispatch(restoreProductionElementsQueuesStaffing(transformedData ?? {}));
    } catch (e) {
      // error message provided by response provider
      dispatch(loadProductionElementsQueuesStaffingFailed());
    }
  };

  const loadIntradayWorkContributionDataOfProductionSchedule = async () => {
    /**
     * Queues staffing of prod. elements from other schedules is not stored on BE side
     * for intraday forecast source data, therefore, we need to calculate it manually on our side.
     */
    const requestsForWorkContribution = composerState.productionScheduleElementsCoverage.filter(
      (coverageRange) =>
        // We don't need to load work contribution for single-queue schedules!
        coverageRange.queueIds.length > 1 &&
        areIntervalsOverlapping(
          { start: pickedTime, end: endTime },
          { start: coverageRange.startDateTime, end: coverageRange.endDateTime }
        )
    );

    if (requestsForWorkContribution.length === 0) {
      // Current day doesn't contain any production schedule elements data
      if (!isEmpty(composerState.productionElementsWorkContributionData)) {
        // We should release previous contribution data from state
        dispatch(calculateProductionElementsWorkContribution({}));
      }
      return;
    }

    dispatch(loadProductionElementsWorkContribution());

    const allQueueIds: string[] = [];

    try {
      const results = await Promise.all(
        requestsForWorkContribution.map(async (requestParameters) => {
          // If original interval extends to previous/next days, it should be cropped to current day only
          const startDate = Math.max(requestParameters.startDateTime, pickedTime);
          const endDate = Math.min(requestParameters.endDateTime, endTime);

          allQueueIds.push(...requestParameters.queueIds);

          return client.query<CalculateIntradayWorkContributionQuery>({
            query: CalculateIntradayWorkContributionDocument,
            variables: {
              parameters: {
                timezone: viewedTimezone,
                agentIds: requestParameters.agentIds,
                dateTimeRange: {
                  endDateTime: new Date(endDate).toISOString(),
                  startDateTime: new Date(startDate).toISOString(),
                },
                queueIds: requestParameters.queueIds,
              },
            },
            fetchPolicy: 'no-cache',
          });
        })
      );

      const sourceData = requestsForWorkContribution.map((requestParameters, index) => ({
        scheduleId: requestParameters.scheduleId,
        data: results[index].data.calculatedIntradayWorkContribution ?? [],
      }));
      const transformedData = transformProductionElementsWorkContributionData(sourceData, composerState.agents);

      dispatch(calculateProductionElementsWorkContribution(transformedData));
    } catch (e) {
      // error message provided by response provider
      dispatch(loadProductionElementsWorkContributionFailed());
    }
  };

  const loadEstimatedServiceLevelData = async () => {
    if (
      composerState.schedulingChartType !== SchedulingChartType.SLA ||
      !composerState.schedulingChartState.dataPerQueue
    ) {
      return;
    }

    dispatch(loadEstimatedServiceLevel());

    try {
      const result = await client.query<CalculateEstimatedServiceLevelPerQueueQuery>({
        query: CalculateEstimatedServiceLevelPerQueueDocument,
        variables: {
          endDateTime: new Date(endTime).toISOString(),
          scheduledStaffingPerQueue: transformCalculatedQueuesStaffingData(
            composerState.schedulingChartState.dataPerQueue
          ),
          startDateTime: new Date(pickedTime).toISOString(),
        } as CalculateEstimatedServiceLevelPerQueueQueryVariables,
        fetchPolicy: 'no-cache',
      });

      const transformedData = recordBy(result.data.estimatedServiceLevelPerQueue.eslQueues, 'queueId');
      let countOfQueueWithMissingBenchmarkData = 0;

      result.data.estimatedServiceLevelPerQueue.eslQueues.forEach((dataPerQueue) => {
        transformedData[dataPerQueue.queueId].series.benchmark = dataPerQueue.series.benchmark.map(([x, y]) => [
          x,
          y ?? -1,
        ]);

        if (!transformedData[dataPerQueue.queueId].series.benchmark.length) {
          countOfQueueWithMissingBenchmarkData += 1;
        }

        const transformedCurrentData: ChartData[] = [];

        dataPerQueue.series.current.forEach(([x, y]) => {
          transformedCurrentData.push([x, isNil(y) ? -1 : y]);
        });

        transformedData[dataPerQueue.queueId].series.current = transformedCurrentData;
      });

      // toast notifications & change to production forecast should be evaluated only when the calculation of ESL was triggered by following 2 actions, other actions should have 0 effect on this
      if (
        countOfQueueWithMissingBenchmarkData &&
        (
          [SchedulingAction.UPDATE_DATE, SchedulingAction.CHANGE_SOURCE_OF_FORECAST] as Values<
            typeof SchedulingAction
          >[]
        ).includes(composerState.schedulingAction)
      ) {
        if (countOfQueueWithMissingBenchmarkData === queueIds.length) {
          // no queue with defined SLA is present
          if (isMultiQueueSchedule) {
            toast.warning(t('schedule.loadOfEstimatedServiceLevelFailedForAllQueues'));
          } else {
            toast.warning(t('schedule.loadOfEstimatedServiceLevelFailed'));
          }

          // switch view back to production forecast as no SLA data are present for any queue
          dispatch(changeSourceOfForecast(SchedulingChartType.Production, ForecastType.Production));
        } else {
          // not all queues are missing SLA, therefore a customized message should be shown
          toast.warning(t('schedule.loadOfEstimatedServiceLevelFailedForSomeQueues'));
        }
      }

      dispatch(calculateEstimatedServiceLevel(transformedData));
    } catch (error) {
      // error message provided by response provider
      dispatch(loadEstimatedServiceLevelFailed());
    }
  };

  useEffect(() => {
    if (
      composerState.schedulingChartType !== SchedulingChartType.SLA ||
      composerState.forecastDataType === ForecastType.Intraday
    ) {
      return;
    }

    loadEstimatedServiceLevelData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerState.schedulingChartState.dataPerQueue, composerState.schedulingChartType]);

  const redirectToFirstStep = () => {
    const { schedulingStartDateTime, schedulingEndDateTime } = location.state.formValues ?? {};

    history.push({
      pathname: getPathname(viewType, id),
      state: {
        scheduleState: {
          allAgentsAreActive,
          schedule,
          viewedTimezone,
          isDirty: composerState.isDirty,
          scheduledAgents: getScheduledAgents(composerState.selectedAgentIds),
          usedScheduleElements: composerState.usedScheduleElements,
          scheduleDaysSettings: getDaysSettings(composerState.daysSettings),
          autoScheduleOptions: composerState.autoScheduleOptions,
          autoScheduleSelectedRange: composerState.autoScheduleSelectedRange,
          initialStaffingPerQueue: composerState.initialQueuesStaffingData,
          usePrecalculatedQueuesStaffing: composerState.usePrecalculatedQueuesStaffing,
        },
        oldFormValues: {
          ...location.state.formValues,
          schedulingStartDateTime:
            schedulingStartDateTime && utcToZonedTime(new Date(schedulingStartDateTime), timezone),
          schedulingEndDateTime: schedulingEndDateTime && utcToZonedTime(new Date(schedulingEndDateTime), timezone),
        },
        forceRefetchSchedule: undefined,
      },
    });
  };

  const handleSubmit: () => Promise<void | string> = async () => {
    const { name, availableSeats } = location.state.formValues as Required<FormValues>;

    const updatedElements = composerState.usedScheduleElements.map((usedScheduleElement) => ({
      agentId: usedScheduleElement.agentId,
      dateTimeRange: {
        endDateTime: new Date(usedScheduleElement.end).toISOString(),
        startDateTime: new Date(usedScheduleElement.start).toISOString(),
      },
      pinned: usedScheduleElement.pinned,
      scheduleElementId: usedScheduleElement.scheduleElementId,
    }));

    const variables: CreateScheduleMutationVariables = {
      schedule: {
        info: {
          name,
          queueIds,
          timezone,
          availableSeats: availableSeats ? Number(availableSeats) : undefined,
          author: 'Guest', // TODO: provide proper user identifier
          notes: '',
          scheduleRange: {
            endDateTime: new Date(endDateTime).toISOString(),
            startDateTime: new Date(startDateTime).toISOString(),
          },
        },
        scheduleItems: updatedElements,
        scheduledAgents: getScheduledAgents(composerState.selectedAgentIds),
        daysSettings: getDaysSettings(composerState.daysSettings),
      },
      allowInactiveAgents: true,
    };

    try {
      if (isEdit && id) {
        await updateSchedule({ variables: { ...variables, id } });
        dispatch(resetDirtyFlag());
        // Opt-out from batching and re-render so PromptModal will not show
        flushSync(() => {
          setShouldBlockLeave(false);
        });
      } else {
        const response = await createSchedule({
          variables,
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: GetSchedulesDocument,
              variables: defaultGetSchedulesVariables,
            },
          ],
        });

        dispatch(resetDirtyFlag());
        setShouldBlockLeave(false);
        return await Promise.resolve(response.data?.createSchedule?.id);
      }
    } catch (error) {
      // error message provided by response provider
      const errorCode = pathOr(null, ['networkError', 'result', 'errorCode'], error);
      if (errorCode === 'fe.sch.prod.items.overlapping') {
        // Scheduling service detected unresolved conflict with production elements, need to reload productionElements and perform check again
        const updatedProductionElements = await loadProductionScheduleElementsAndRestore();
        checkForPossibleConflictsWithProductionSchedule(updatedProductionElements);
      }

      setShouldBlockLeave(shouldBlockLeave);

      throw error;
    }

    return Promise.resolve();
  };

  const initUpdateProductionState = async () => {
    if (allAgentsAreActive || isProduction) {
      await handleUpdateProductionState();
    } else if (isCreate) {
      toast.warning(t('schedule.unableToPutToProductionWithNotAssignedAgents'));
    } else {
      setInactiveAgentModal({
        open: true,
        customSubmitHandler: handleUpdateProductionState,
        customTitle: t('layout.action.putToProduction'),
        onSubmitSuccess: () => {
          if (!isEdit) {
            setShouldSwitchToEdit(true);
          }
          setInactiveAgentModal({
            open: false,
          });
        },
        onSubmitError: () => null,
      });
    }
  };

  useEffect(() => {
    if (!isGanttRendered) {
      // Prevents executing on scene load
      return;
    }

    if (
      composerState.schedulingChartType === SchedulingChartType.SLA &&
      composerState.forecastDataType === ForecastType.Production
    ) {
      checkIfProductionForecastIsPresent(composerState.forecast);

      // queues staffing should be already calculated when switching to SLA type, no need to recalculate it
      return;
    }

    if (
      composerState.schedulingChartType === SchedulingChartType.Production &&
      composerState.forecastDataType === ForecastType.Production
    ) {
      // forecast and queues staffing should be already evaluated when switching from SLA type back to Production forecast view, no need to re-fetch the data
      return;
    }

    dispatch(loadForecastData());
    loadForecast();
    loadWorkContributionData();

    if (composerState.schedulingChartType === SchedulingChartType.Intraday) {
      loadIntradayWorkContributionDataOfProductionSchedule();
    } else {
      // we need to calculate queues staffing for both production forecast and SLA
      loadQueuesStaffingOfOtherProductionElements();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerState.schedulingChartType]);

  const handleUpdateProductionState = async () => {
    const newState = isProduction ? StateEnum.NonProduction : StateEnum.Production;
    let scheduleId = id;

    try {
      if (isCreate) {
        // create schedule before putting to production
        const createdId = await handleSubmit();
        scheduleId = createdId || undefined; // avoids null as possible return value
      } else if (isEdit && composerState.isDirty && !isProduction) {
        // update schedule before putting to production
        await handleSubmit();
      }

      if (isProduction && scheduleId) {
        await removeScheduleFromProduction({
          variables: {
            scheduleId,
          },
          update: (cache) => {
            // update cache of existing schedule
            if (id) {
              const data = cache.readQuery<GetScheduleQuery>({
                query: GetScheduleDocument,
                variables: {
                  id,
                },
              });

              if (data?.schedule) {
                cache.writeQuery<GetScheduleQuery>({
                  query: GetScheduleDocument,
                  variables: {
                    id,
                  },
                  data: {
                    schedule: {
                      ...data.schedule,
                      info: {
                        ...data.schedule.info,
                        state: newState,
                      },
                    },
                  },
                });
              }
            }
          },
        });
      } else if (!isProduction && scheduleId) {
        await putScheduleToProduction({
          variables: {
            scheduleId,
          },
          update: (cache) => {
            // update cache of existing schedule
            if (id) {
              const data = cache.readQuery<GetScheduleQuery>({
                query: GetScheduleDocument,
                variables: {
                  id,
                },
              });

              if (data?.schedule) {
                cache.writeQuery<GetScheduleQuery>({
                  query: GetScheduleDocument,
                  variables: {
                    id,
                  },
                  data: {
                    schedule: {
                      ...data.schedule,
                      info: {
                        ...data.schedule.info,
                        state: newState,
                      },
                    },
                  },
                });
              }
            }
          },
        });
      }

      if (isEdit && composerState.isDirty && isProduction) {
        // update schedule after removing from production
        await handleSubmit();
      }

      history.replace({
        pathname: getComposerPathname(CreateEditView.EDIT, scheduleId),
        state: {
          ...location.state,
          formValues: {
            ...location.state.formValues,
            id: scheduleId,
            state: newState,
          },
          scheduleState: {
            ...location.state.scheduleState,
            schedule: {
              info: {
                ...location.state.scheduleState?.schedule?.info,
                id: scheduleId,
                state: newState,
              },
            },
            isDirty: false,
          },
        },
      });

      const lockedAgentsIdsObj: Record<string, boolean> = {}; // Default will remove all previously locked agents

      if (newState === StateEnum.Production) {
        // eslint-disable-next-line no-return-assign
        composerState.selectedAgentIds.forEach((selectedAgentId) => (lockedAgentsIdsObj[selectedAgentId] = true));
      }

      dispatch(setLockedAgentsIds(lockedAgentsIdsObj));
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleToggleScheduleConfiguration = () => {
    if (isScheduleConfigurationSidebarOpen) {
      closeSidebar();
      setIsScheduleConfigurationSidebarOpen(false);
    } else {
      setIsScheduleConfigurationSidebarOpen(true);

      if (!isSidebarOpen) {
        openSidebar();
      }
    }
  };

  const handleToggleAddPersonsSidebar = () => {
    if (!isSidebarOpen) {
      openSidebar();
      setIsScheduleConfigurationSidebarOpen(false);
    } else if (isScheduleConfigurationSidebarOpen) {
      setIsScheduleConfigurationSidebarOpen(false);
    } else {
      toggleSidebar();
    }
  };

  const handleCancelScheduleConfigurationSidebar = () => {
    closeSidebar();
    setIsScheduleConfigurationSidebarOpen(false);
  };

  const handleCreateAutoScheduleJob = async () => {
    // No agents are selected/present in schedule thus add all available agents to schedule
    if (!composerState.selectedAgentIds.length) {
      const agentsIds = agents.filter((agent) => agent.active).map((agent) => agent.id);

      dispatch(selectAgents(agentsIds));

      setShouldTriggerAutoSchedule(true);
    } else {
      triggerCreateAutoScheduleJob();
    }
  };

  const triggerCreateAutoScheduleJob = async (selectedAgentIds: string[] = []) => {
    try {
      let scheduleId = id;

      if (isScheduleConfigurationSidebarOpen) {
        handleCancelScheduleConfigurationSidebar();
      }

      if (isCreate || (isEdit && composerState.isDirty)) {
        const submitResponse = await handleSubmit(); // Success response is string scheduleId of newly saved schedule

        if (typeof submitResponse === 'string') {
          // New schedule was created
          scheduleId = submitResponse;
          setShouldSwitchToEdit(true);
        }
      }

      if (scheduleId) {
        const { autoScheduleOptions, autoScheduleSelectedRange, schedulingChartType } = composerState;

        const selectedRange = {
          startDateTime: zonedTimeToUtc(new Date(autoScheduleSelectedRange.startDateTime), timezone).toISOString(),
          endDateTime: zonedTimeToUtc(new Date(autoScheduleSelectedRange.endDateTime), timezone).toISOString(),
        };

        const createAutoScheduleJobResponse = await createAutoScheduleJob({
          variables: {
            autoScheduleJob: {
              scheduleId,
              scheduleOptions: autoScheduleOptions,
              selectedRange,
              selectedAgentIds,
              forecastType:
                schedulingChartType === SchedulingChartType.SLA
                  ? ForecastType.Production // when SLA displayed, production forecast should be provided as the initial data set for autoscheduler
                  : (schedulingChartType as ForecastType),
              strictSelectedRange: autoScheduleSelectedRange.strictSelectedRange,
            },
          },
        });

        const autoScheduleJob = createAutoScheduleJobResponse.data?.createAutoScheduleJob;

        if (autoScheduleJob) {
          const autoScheduleJobState = {
            correlationId: autoScheduleJob.correlationId,
            scheduleId: autoScheduleJob.scheduleId,
            state: autoScheduleJob.state,
          };

          const autoScheduleJobInfo = {
            [scheduleId]: { name: scheduleName },
          };

          addNewSchedulesToWatch([autoScheduleJobState], autoScheduleJobInfo);
        }
      }
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  /**
   * Auto Schedule trigger is available only for active/valid agents
   * Non valid state is when all selected agents are deleted, not active, without shiftTemplate or re-assigned to other queue
   * empty agents list is also not valid scenario
   * only one auto schedule calc at the time is possible for user
   * schedule endTime range must be in future
   * performance limit 100 agents and 2 weeks range must be not exceeded
   */
  const checkAutoScheduleJobAvailable = () => {
    if (activeSchedulesIds.length || !scheduleEndsInFuture || isAutoScheduleLimitExceeded) {
      return false;
    }

    const { selectedAgentIds } = composerState;

    if (selectedAgentIds.length !== 0) {
      const nonValidAgentsIds = selectedAgentIds.filter((agentId) => {
        const currentAgent = composerState.agents[agentId];
        return (
          currentAgent.isNotAssigned ||
          currentAgent.deleted ||
          !currentAgent.active ||
          !currentAgent.shiftTemplateIds?.length
        );
      });

      if (nonValidAgentsIds.length === selectedAgentIds.length) {
        return false;
      }

      return true;
    }

    if (agents.length && agents.filter((agent) => agent.active).length) {
      return true;
    }

    return false;
  };

  const headerProps: HeaderProps = {
    isEdit,
    isProduction,
    isView,
    lockType,
    activeSchedulesIds,
    isScheduleConfigurationSidebarOpen,
    handleToggleScheduleConfiguration,
    handleToggleAddPersonsSidebar,
    scheduleEndsInFuture,
    isAutoScheduleLimitExceeded,
    isAutoScheduleRangeOverlapping,
    existAgentWithAssignedShiftTemplate,
    handleCreateAutoScheduleJob: checkAutoScheduleJobAvailable() ? handleCreateAutoScheduleJob : undefined,
    pickedTimeLocal: pickedTimeLocal.getTime(),
    pickedTimeViewed: pickedTime,
    timezone: viewedTimezone,
    action: viewType,
    handleSubmit: async () => {
      if (allAgentsAreActive) {
        try {
          await handleSubmit();
          if (!isEdit) {
            setShouldSwitchToEdit(true);
          }
        } catch (e) {
          // `customFetch()` will display error message
        }
      } else if (isCreate && composerState.notAssignedAgentIdsWithScheduleElement.length) {
        toast.warning(t('schedule.unableToSaveWithNotAssignedAgents'));
      } else {
        setInactiveAgentModal({
          open: true,
          onSubmitSuccess: () => {
            if (!isEdit) {
              setShouldSwitchToEdit(true);
            }
            setInactiveAgentModal({
              open: false,
            });
          },
          onSubmitError: () => null,
        });
      }
    },
    handlePreviousStep: () => setGoToFirstStep(true),
    updateProductionState: initUpdateProductionState,
    endTime: extendedScheduleRangeEndInLocalTimezone.getTime(),
    scheduleId: id,
    startTime: startDateInLocalTimezone.getTime(),
    scheduleHistory: composerState.scheduleHistory,
  };

  const hasSomeWorkContributionData = hasWorkContributionData(composerState);
  const noSupervisedAgentsSelected = !filterSupervisedAgentIds(
    composerState.selectedAgentIdsSorted,
    composerState.supervisedAgentsRecordObj,
    composerState.notAssignedAgentIdsWithScheduleElement
  ).length;
  const noSupervisedAgentsInSchedule = noSupervisedAgentsSelected && !composerState.visibleSupervisedAgentIds.length;
  const noAgentVisible = !noSupervisedAgentsInSchedule && !composerState.visibleSupervisedAgentIds.length;

  const scheduleSavedName = schedule && schedule.info.name;
  const scheduleFormName = pathOr('', ['state', 'formValues', 'name'], location);
  const scheduleName: string = scheduleFormName || scheduleSavedName;

  const renderNoAgentInQueueMessage = isEmpty(composerState.agents) && (
    <FullSizeMessage iconKind="conversation-video" headline={t('schedule.noPeopleAddedIntoQueue')} />
  );

  const renderNoAgentInScheduleMessage = noSupervisedAgentsSelected && !hasSomeWorkContributionData && (
    <FullSizeMessage iconKind="conversation-video" headline={t('schedule.noPeopleSelected')} />
  );

  const conflictsWithProductionScheduleElementsDetectedMessage = elementsInCollisionModal.state === 'open' && (
    <FullSizeMessage
      iconKind="warning"
      headline={t('schedule.modal.collisionWithElementsBody', {
        count: Math.max(
          elementsInCollisionModal.productionSchedulesInConflict.length,
          elementsInCollisionModal.ptoRequestsInConflict.length
        ),
      })}
    />
  );

  const renderMessage =
    renderNoAgentInQueueMessage ||
    renderNoAgentInScheduleMessage ||
    conflictsWithProductionScheduleElementsDetectedMessage;

  // reset scroll position when removing all agents from gantt
  if (!isLoading && noSupervisedAgentsSelected) {
    setScrollPositionInStorage();
  }

  const onResizeHandler = useCallback(() => {
    if (schedulingChartScrollbarRef && schedulingChartScrollbarRef.current) {
      schedulingChartScrollbarRef.current.forceUpdate();
    }

    if (ganttChartScrollbarRef && ganttChartScrollbarRef.current) {
      ganttChartScrollbarRef.current.forceUpdate();
    }

    if (yAxisScrollbarRef && yAxisScrollbarRef.current) {
      yAxisScrollbarRef.current.forceUpdate();
    }

    if (timelineScrollbarRef && timelineScrollbarRef.current) {
      timelineScrollbarRef.current.forceUpdate();
    }

    if (ganttChartContainerRef && ganttChartContainerRef.current) {
      const ganttTopOffsetCurrent = ganttChartContainerRef.current.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;

      // 40px margin bottom
      const offset = windowHeight - ganttTopOffsetCurrent - 40;

      // 15px height of scrollbar
      const enhancedOffset = offset - ((offset - 15) % STATIC_ELEMENT_HEIGHT);
      setGanttTopOffset(enhancedOffset);
    }
  }, []);

  const handleMouseUp = (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
    if (draggedElement) {
      handleMouseUpUtil(draggedElement, composerState, dispatch, chartRef, e.nativeEvent);
    }
  };

  const handleRemoveElements = (e: KeyboardEvent) => {
    if (!isView && includes(e.key, REMOVE_ELEMENT_KEYS)) {
      const selectedElements = chartRef.current ? chartRef.current.chart.getSelectedPoints() : [];

      if (selectedElements.length) {
        /**
         * When removing last elements from chart, DnD handler would remain active and break scene.
         * Remove points via their API in order to correctly unregister DnD events.
         */
        selectedElements.forEach((point) => point.remove(false));
        dispatch(removeElements());
      }
    }
  };

  const onScheduleElementDragStartHandler = (elementId: string) => {
    const scheduleElement = composerState.scheduleElements.find((element) => element.id === elementId);

    if (scheduleElement) {
      setDraggedElement(scheduleElement);
    }
  };

  const onScheduleElementDragEndHandler = () => {
    if (draggedElement) {
      setDraggedElement(null);
    }
  };

  const handleScheduleElementDrop = (e: PointDropEventObject) => {
    const updatedPoints = Object.values(e.newPoints).map((newPoint) => newPoint.point) as Array<
      Point & ChartScheduleElement
    >;

    dispatch(updateElements(updatedPoints, composerState.usedScheduleElementsWithinCurrentDay));

    return false;
  };

  function handleToggleSelectScheduleElement(e: PointInteractionEventObject) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { key } = this as Point;
    const isMultipleSelection = e.accumulate;

    dispatch(toggleSelectScheduleElementsState([key], isMultipleSelection));

    return false;
  }

  const enhancedChartOptions: HighchartsOptions = {
    ...composerState.chartState,
    plotOptions: {
      ...composerState.chartState.plotOptions,
      series: {
        ...composerState.chartState.plotOptions!.series,
        allowPointSelect: !isView,
        dragDrop: {
          ...composerState.chartState.plotOptions!.series!.dragDrop,
          draggableX: !isView,
          draggableY: !isView,
        },
        point: {
          ...(composerState.chartState.plotOptions!.series as SeriesOptionsType).point,
          events: {
            ...(composerState.chartState.plotOptions!.series as SeriesOptionsType).point!.events,
            drag: (event) => {
              const handler = handleElementDragDrop(
                composerState.agents,
                composerState.visibleSupervisedAgentIds, // Should not take not-supervised agents into the account as they are not visible for the supervisor anyway
                pickedTime,
                endTime,
                composerState.nearestSurroundingDaysElements,
                (composerState.chartState.series as SeriesGanttOptions[])[2].data as ChartScheduleElement[],
                composerState.nearestSurroundingDaysProductionElements
              );
              draggedState.current.shouldAllowDrop = handler(event);
              return draggedState.current.shouldAllowDrop;
            },
            drop: handleScheduleElementDrop,
            select: handleToggleSelectScheduleElement,
            unselect: handleToggleSelectScheduleElement,
          },
        },
      },
    },
  };

  // Right scroll
  const updateScrollPositionOfSchedulingChartRight = (e: HTMLElement) => {
    if (!isLoadingForecastOrWorkContribution && schedulingChartContainerRef!.scrollLeft < e.scrollLeft) {
      schedulingChartContainerRef!.scrollLeft = e.scrollLeft;
    }
  };

  const updateScrollPositionOfGanttTimelineRight = (e: HTMLElement) => {
    if (timelineScrollbarContainerRef!.scrollLeft < e.scrollLeft) {
      timelineScrollbarContainerRef!.scrollLeft = e.scrollLeft;
    }
  };

  // We needed to provide debounce of scrollUpdateHandler so we prevent lagging of the Gantt chart's scrollbar
  const updateScrollPositionOfGanttChartRight = debounce((e: HTMLElement) => {
    if (ganttChartScrollbarContainerRef!.scrollLeft < e.scrollLeft) {
      ganttChartScrollbarContainerRef!.scrollLeft = e.scrollLeft;
    }
  });

  const onGanttTimelineXPositionChangeRight = (e: HTMLElement) => {
    updateScrollPositionOfGanttChartRight(e);
    updateScrollPositionOfSchedulingChartRight(e);
  };

  const onGanttChartXPositionChangeRight = (e: HTMLElement) => {
    updateScrollPositionOfGanttTimelineRight(e);
    updateScrollPositionOfSchedulingChartRight(e);
  };

  const onSchedulingChartXPositionChangeRight = (e: HTMLElement) => {
    updateScrollPositionOfGanttTimelineRight(e);
    updateScrollPositionOfGanttChartRight(e);
  };

  // Left scroll
  const updateScrollPositionOfSchedulingChartLeft = (e: HTMLElement) => {
    if (!isLoadingForecastOrWorkContribution && schedulingChartContainerRef!.scrollLeft > e.scrollLeft) {
      schedulingChartContainerRef!.scrollLeft = e.scrollLeft;
    }
  };

  const updateScrollPositionOfGanttTimelineLeft = (e: HTMLElement) => {
    if (timelineScrollbarContainerRef!.scrollLeft > e.scrollLeft) {
      timelineScrollbarContainerRef!.scrollLeft = e.scrollLeft;
    }
  };

  // We needed to provide debounce of scrollUpdateHandler so we prevent lagging of the Gantt chart's scrollbar
  const updateScrollPositionOfGanttChartLeft = debounce((e: HTMLElement) => {
    if (ganttChartScrollbarContainerRef!.scrollLeft > e.scrollLeft) {
      ganttChartScrollbarContainerRef!.scrollLeft = e.scrollLeft;
    }
  });

  const onGanttTimelineXPositionChangeLeft = (e: HTMLElement) => {
    updateScrollPositionOfGanttChartLeft(e);
    updateScrollPositionOfSchedulingChartLeft(e);
  };

  const onGanttChartXPositionChangeLeft = (e: HTMLElement) => {
    updateScrollPositionOfGanttTimelineLeft(e);
    updateScrollPositionOfSchedulingChartLeft(e);
  };

  const onSchedulingChartXPositionChangeLeft = (e: HTMLElement) => {
    updateScrollPositionOfGanttTimelineLeft(e);
    updateScrollPositionOfGanttChartLeft(e);
  };

  const restoreCurrentScrollXPosition = throttle((e: HTMLElement) => {
    const scrollPosition = getScrollPositionFromStorage();

    if (ganttChartScrollbarContainerRef && scrollPosition.x !== e.scrollLeft) {
      setScrollPositionInStorage({ x: e.scrollLeft, y: scrollPosition.y });
    }
  }, 1000);

  const restoreCurrentScrollYPosition = throttle((e: HTMLElement) => {
    const scrollPosition = getScrollPositionFromStorage();

    if (ganttChartScrollbarContainerRef && scrollPosition.y !== e.scrollTop) {
      setScrollPositionInStorage({ x: scrollPosition.x, y: e.scrollTop });
    }
  }, 1000);

  const onScrollXHandler = (callback: (e: HTMLElement) => void) => (e: HTMLElement) => {
    callback(e);
    restoreCurrentScrollXPosition(e);
  };

  const onScrollYHandlerDown = (container: HTMLElement | null) => (e: HTMLElement) => {
    if (container && container.scrollTop < e.scrollTop) {
      container.scrollTop = e.scrollTop;
      restoreCurrentScrollYPosition(e);
      getOnlyAgentsInViewPort();
      restoreCursorPositionWithinGanttChart();
    }
  };

  const onScrollYHandlerUp = (container: HTMLElement | null) => (e: HTMLElement) => {
    if (container && container.scrollTop > e.scrollTop) {
      container.scrollTop = e.scrollTop;
      restoreCurrentScrollYPosition(e);
      getOnlyAgentsInViewPort();
      restoreCursorPositionWithinGanttChart();
    }
  };

  const restoreCursorPositionWithinGanttChart = () => {
    if (!chartRef.current || !mouseMoveStateRef.current) {
      return;
    }

    const { clientX, clientY } = mouseMoveStateRef.current;
    if (clientX === 0 && clientY === 0) {
      return;
    }

    // Create new mouse event
    const eventToDispatch = new MouseEvent('mousemove', {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX,
      clientY,
    });

    chartRef.current.chart.container.dispatchEvent(eventToDispatch);
  };

  const getOnlyAgentsInViewPort = debounce(() => {
    if (
      !composerState.visibleSupervisedAgentIds.length ||
      !ganttWrapperRef?.current ||
      !isGanttRendered ||
      !chartRef.current
    ) {
      return; // empty array
    }

    /**
     * TODO: This value could be dynamically computed based on chart's height.
     */
    const gutter = 5;

    const { height } = ganttWrapperRef.current.getBoundingClientRect();
    const numberOfVisibleAgents = Math.round(height / STATIC_ELEMENT_HEIGHT) + gutter;

    const top = yAxisScrollbarContainerRef?.scrollTop || 0;
    // (closest divisor that is equal or less than top position) divided by height of one label -> start index
    const startIndex = Math.round((top - (top % STATIC_ELEMENT_HEIGHT)) / STATIC_ELEMENT_HEIGHT);
    const startIndexWGutter = startIndex > gutter ? startIndex - gutter : 0;

    const result = composerState.visibleSupervisedAgentIds.slice(startIndexWGutter, startIndex + numberOfVisibleAgents);

    const { chart } = chartRef.current;

    const agentIdsToKeep: Set<string> = new Set(result);
    const draggedPoints = cloneDeep((chart as any).dragDropData?.groupedPoints);
    draggedPoints?.forEach((point: ChartScheduleElement) => agentIdsToKeep.add(point.agentId));

    if (!result.length) {
      return;
    }

    dispatch(reloadChartData(Array.from(agentIdsToKeep)));
  }, 50);

  const setScrollPosition = (position: ScrollPosition) => {
    if (
      ganttChartScrollbarContainerRef &&
      schedulingChartContainerRef &&
      timelineScrollbarContainerRef &&
      yAxisScrollbarContainerRef
    ) {
      // update horizontal position
      ganttChartScrollbarContainerRef.scrollLeft = position.x;
      schedulingChartContainerRef.scrollLeft = position.x;
      timelineScrollbarContainerRef.scrollLeft = position.x;

      // update vertical position
      ganttChartScrollbarContainerRef.scrollTop = position.y;
      yAxisScrollbarContainerRef.scrollTop = position.y;
    }
  };

  const getDefaultElementLengthMessage = (length: string) => t('form.defaultLength', { length });
  const isDataPreparing = isEmpty(composerState.queues) || elementsInCollisionModal.state === 'loading';

  const onTimezoneChangeHandler = async (newTimezone: string) => {
    const isTimezoneMatchingOriginal = timezone === newTimezone;

    let newMinDate = startOfDayInZone(zonedTimeToUtc(pickedTimeLocal, newTimezone), newTimezone);
    let newMaxDate = startOfDayInZone(zonedTimeToUtc(endTimeLocal, newTimezone), newTimezone);

    const startOfScheduleRangeInNewTZ = isTimezoneMatchingOriginal
      ? new Date(startDateTime)
      : utcToZonedTime(new Date(startDateTime), newTimezone);
    const startOfFirstDayOfScheduleRangeInNewTZ = startOfDayInZone(startOfScheduleRangeInNewTZ, newTimezone);

    const endOfScheduleRangeInNewTZ = isTimezoneMatchingOriginal
      ? extendedScheduleRangeEnd
      : utcToZonedTime(extendedScheduleRangeEnd, newTimezone);
    const startOfLastDayOfScheduleRangeInNewTZ = startOfDayInZone(
      handleEndOfDateRange(endOfScheduleRangeInNewTZ),
      newTimezone
    );

    if (newMinDate < startOfFirstDayOfScheduleRangeInNewTZ) {
      // prevent from getting out of range (day before start of schedule range)
      newMinDate = startOfFirstDayOfScheduleRangeInNewTZ;
      newMaxDate = startOfDayInZone(addDays(startOfFirstDayOfScheduleRangeInNewTZ, 1), newTimezone);
    } else if (newMinDate > startOfLastDayOfScheduleRangeInNewTZ) {
      // prevent from getting out of range (day after end of schedule range)
      newMinDate = startOfLastDayOfScheduleRangeInNewTZ;
      newMaxDate = startOfDayInZone(addDays(startOfLastDayOfScheduleRangeInNewTZ, 1), newTimezone);
    }

    // changeTimezone action will remove content of clipboard if existed
    if (composerState.clipboard.isPasteScheduleActive) {
      toast.info(t('schedule.contentOfClipboardHasBeenReset'));
    }

    toast.success(t('timezone.changingViewTo', { name: formatTimezoneIdToName(newTimezone) }));

    dispatch(changeTimezone(newTimezone, newMinDate.getTime(), newMaxDate.getTime()));
  };

  const shouldShowLoader =
    isLoadingForecastOrWorkContribution ||
    isDataPreparing ||
    (!isGanttRendered && !renderMessage && !noSupervisedAgentsInSchedule);

  const renderContent = (
    <ReactResizeDetector handleWidth handleHeight onResize={onResizeHandler}>
      {() => (
        <Container className="gantt-container container--wide-content" data-test-id={CONTAINER_TAG}>
          {showSeatsAlert && (
            <Alert dataTestId="alert-more-people-than-seats" className="mb-2">
              {t('schedule.morePeopleThanSeats')}
            </Alert>
          )}
          <SchedulingChartContainer
            endTime={endTime}
            schedulingChartType={composerState.schedulingChartType}
            estimatedServiceLevelPerQueue={composerState.estimatedServiceLevelPerQueue}
            isLoadingForecastData={shouldShowLoader}
            isLoadingSlaData={composerState.isLoadingEstimatedServiceLevel}
            missingForecastIntervalsPerQueue={composerState.missingForecastIntervalsPerQueue}
            onScrollXHandler={onScrollXHandler}
            onSchedulingChartXPositionChangeLeft={onSchedulingChartXPositionChangeLeft}
            onSchedulingChartXPositionChangeRight={onSchedulingChartXPositionChangeRight}
            onToggleSourceOfForecastData={(schedulingChartType) =>
              dispatch(changeSourceOfForecast(schedulingChartType))
            }
            queueIds={composerState.queueIds}
            queues={composerState.queues}
            scrollbarRef={schedulingChartScrollbarRef}
            setChartContainerRef={setSchedulingChartContainerRef}
            schedulingChartState={composerState.schedulingChartState}
            startTime={pickedTime}
            timezone={viewedTimezone}
            width={chartWidth}
          />
          <div className="drag-drop">
            <div className="settings">
              <div className="dropdowns">
                <TimezoneSwitcher
                  agents={composerState.agents}
                  selectedAgentsIds={composerState.selectedAgentIdsSorted}
                  allTimezones={allTimezones}
                  date={endDateTime}
                  onTimezoneChange={onTimezoneChangeHandler}
                  queues={composerState.queues}
                  scheduleTimezone={timezone}
                  usedQueuesIds={composerState.queueIds}
                  viewedTimezone={viewedTimezone}
                />
                <SettingsDropdown
                  dispatch={dispatch}
                  ganttScheduleElements={composerState.usedScheduleElementsWithinCurrentDay}
                  isView={isView}
                  pickedDayIndex={composerState.pickedDayIndex}
                  pinnedScheduleElementsIds={composerState.pinnedScheduleElementsIds}
                  pinnedUsedScheduleElementsIds={composerState.pinnedUsedScheduleElementsIds}
                  selectedScheduleElementsIds={composerState.selectedScheduleElementsIds}
                  totalPinnableElementIds={totalPinnableElementIds}
                  setCloneMenuOpened={noAgentVisible ? undefined : setCloneMenuOpened}
                  settings={currentDaySettings}
                  // Needs to be changed when more settings are present
                  showActiveIndicator={currentDaySettings?.hideNonWorkingAgents}
                  t={t}
                  usedScheduleElements={composerState.usedScheduleElements}
                />
              </div>
              <div className="scheduling-composer__timezone-info">
                <TimezoneName
                  className="scheduling-composer__timezone-info-name"
                  dataTestId="scheduling-composer:viewed-timezone-name"
                  timezone={viewedTimezone}
                />
                <div
                  className="scheduling-composer__timezone-info-offset"
                  data-test-id="scheduling-composer:viewed-timezone-offset"
                >
                  {getUTCHoursOffsetLabel(new Date(endDateTime), viewedTimezone, locale, t)}
                </div>
              </div>
            </div>
            <DragAndDrop
              defaultLengthMessage={getDefaultElementLengthMessage}
              scheduleElements={composerState.scheduleElements}
              isDragDisabled={isView || noAgentVisible}
              onDragStart={onScheduleElementDragStartHandler}
              onDragEnd={onScheduleElementDragEndHandler}
            />
          </div>
          {!noAgentVisible && !noSupervisedAgentsInSchedule ? (
            <div
              className={classnames('scheduling-composer__gantt-section-wrapper', {
                'scheduling-composer__gantt-section-wrapper--hidden': !ganttTopOffset || !isGanttRendered,
              })}
            >
              <PerfectScrollbar
                containerRef={setTimelineScrollbarContainerRef}
                className="gantt-x-axis__wrapper"
                onScrollLeft={onScrollXHandler(onGanttTimelineXPositionChangeLeft)}
                onScrollRight={onScrollXHandler(onGanttTimelineXPositionChangeRight)}
                ref={timelineScrollbarRef}
              >
                <CustomXAxis start={pickedTime} end={endTime} width={chartWidth} timezone={viewedTimezone} />
              </PerfectScrollbar>
              <div className="scheduling-composer__gantt-wrapper" ref={ganttWrapperRef}>
                <PerfectScrollbar
                  containerRef={setYAxisScrollbarContainerRef}
                  className="gantt-y-label__scrollbar-container"
                  onScrollDown={debounce(onScrollYHandlerDown(ganttChartScrollbarContainerRef))}
                  onScrollUp={debounce(onScrollYHandlerUp(ganttChartScrollbarContainerRef))}
                  // 14px equals to height of x-axis scrollbar
                  style={{ maxHeight: ganttTopOffset ? ganttTopOffset - 14 : undefined }}
                  options={{ suppressScrollX: true, scrollYMarginOffset: 5 }}
                  ref={yAxisScrollbarRef}
                >
                  <YLabelList
                    agents={composerState.agents}
                    cloneMenuOpened={cloneMenuOpened}
                    containerRef={yAxisContainerRef}
                    daySettings={currentDaySettings}
                    daySettingsActive={daySettingsActive}
                    dispatch={dispatch}
                    elementIdsPerAgent={elementIdsPerAgent}
                    isAutoScheduleLimitExceeded={isAutoScheduleLimitExceeded}
                    isContextMenuEnabled={!isView}
                    isMultiQueueSchedule={isMultiQueueSchedule}
                    isPasteScheduleActive={composerState.clipboard.isPasteScheduleActive}
                    lockedAgentsIds={composerState.lockedAgentsIds}
                    maxDate={extendedScheduleRangeEndInLocalTimezone}
                    minDate={startDateInLocalTimezone}
                    pickedTime={pickedTimeLocal}
                    queues={composerState.queues}
                    scrollbarContainerRef={yAxisScrollbarContainerRef}
                    selectedAgents={composerState.visibleSupervisedAgentIds}
                    setCloneMenuOpened={setCloneMenuOpened}
                    skills={composerState.skills}
                    triggerCreateAutoScheduleJob={
                      checkAutoScheduleJobAvailable() ? triggerCreateAutoScheduleJob : undefined
                    }
                    usedScheduleElements={pathOr([], ['chartState', 'series', 0, 'data'], composerState)}
                    productionScheduleElements={
                      ((composerState.chartState.series as SeriesGanttOptions[])[2]
                        .data as ProductionChartScheduleElement[]) ?? []
                    }
                  />
                </PerfectScrollbar>
                <div className="scheduling-composer__gantt__outer-container">
                  <PerfectScrollbar
                    ref={ganttChartScrollbarRef}
                    className="scheduling-composer__gantt-container"
                    containerRef={setGanttChartScrollbarContainerRef}
                    onScrollLeft={onScrollXHandler(onGanttChartXPositionChangeLeft)}
                    onScrollRight={onScrollXHandler(onGanttChartXPositionChangeRight)}
                    onScrollDown={onScrollYHandlerDown(yAxisScrollbarContainerRef)}
                    onScrollUp={onScrollYHandlerUp(yAxisScrollbarContainerRef)}
                    options={{ scrollYMarginOffset: 5 }}
                    style={{ maxHeight: ganttTopOffset }}
                  >
                    <div
                      className={classnames('scheduling-composer__gantt__scroller-container', {
                        'drag-active': draggedElement !== null,
                      })}
                    >
                      <div
                        ref={ganttChartContainerRef}
                        onMouseUp={handleMouseUp}
                        data-test-id="scheduling-composer:gantt-chart"
                      >
                        <GanttChart
                          containerRef={ganttChartContainerRef}
                          chartOptions={enhancedChartOptions}
                          chartRef={chartRef}
                          draggedState={draggedState.current}
                          scheduleTimezone={timezone}
                          viewedTimezone={viewedTimezone}
                          wholeScheduleEnd={extendedScheduleRangeEnd.getTime()}
                          wholeScheduleStart={startDateTime}
                        />
                      </div>
                    </div>
                  </PerfectScrollbar>
                </div>
              </div>
              <div className="scheduling-composer__gantt__bottom" />
            </div>
          ) : (
            <>
              {noAgentVisible && (
                <div className="text-center py-8">
                  <FullSizeMessage
                    className="h-auto"
                    iconKind="non-workers-off"
                    headline={t('schedule.allNonWorkingPeopleHidden')}
                  />
                  <Button
                    data-test-id={SETTINGS_SHOW_ALL_AGENTS}
                    onClick={() =>
                      dispatch(updateDaySetting(composerState.pickedDayIndex, 'hideNonWorkingAgents', false))
                    }
                  >
                    {t('person.showAllPeople')}
                  </Button>
                </div>
              )}
              {noSupervisedAgentsInSchedule && (
                <div className="text-center py-8">
                  <FullSizeMessage
                    className="h-auto"
                    iconKind="non-workers-off"
                    headline={t('schedule.noSupervisedAgentsInSchedule')}
                  />
                </div>
              )}
            </>
          )}
          <div id="dragstatus" />
          {inactiveAgentModal.open && (
            <InactiveAgentConfirmationModal
              customTitle={inactiveAgentModal.customTitle}
              isOpen
              isEdit={isEdit}
              isMultiQueueSchedule={isMultiQueueSchedule}
              scheduleName={scheduleName}
              onSubmit={async () => {
                try {
                  if (inactiveAgentModal.customSubmitHandler) {
                    await inactiveAgentModal.customSubmitHandler();
                  } else {
                    await handleSubmit();
                  }
                  inactiveAgentModal.onSubmitSuccess();
                } catch (e) {
                  setInactiveAgentModal({ open: false });
                  inactiveAgentModal.onSubmitError();
                }
              }}
              onClose={() => setInactiveAgentModal({ open: false })}
              t={t}
            />
          )}
        </Container>
      )}
    </ReactResizeDetector>
  );

  return (
    <Content
      header={getHeaderProps(t, dispatch, scheduleName, headerProps)}
      sidebarContent={
        isScheduleConfigurationSidebarOpen ? (
          <ScheduleConfigurationSidebar
            autoScheduleOptions={composerState.autoScheduleOptions}
            autoScheduleSelectedRange={composerState.autoScheduleSelectedRange}
            dispatch={dispatch}
            forecastType={
              composerState.schedulingChartType === SchedulingChartType.SLA
                ? ForecastType.Production // when SLA displayed, production forecast should be provided as the initial data set for autoscheduler
                : (composerState.schedulingChartType as ForecastType)
            }
            handleCancel={handleCancelScheduleConfigurationSidebar}
            maxDate={extendedScheduleRangeEndInLocalTimezone}
            minDate={autoScheduleMinDate}
          />
        ) : (
          <AddPersonsSidebar
            agents={supervisedAgents.filter(
              (agent) => agent.active && !composerState.selectedAgentIds.includes(agent.id)
            )}
            queues={composerState.queues}
            skills={composerState.skills}
            selectAgents={(pickedAgentIds: string[]) => dispatch(selectAgents(pickedAgentIds))}
          />
        )
      }
    >
      <PromptModal
        t={t}
        when={shouldBlockLeave}
        onSave={async ({ proceed, close }) => {
          if (allAgentsAreActive) {
            try {
              await handleSubmit();
              proceed();
            } catch (e) {
              close();
            }
          } else {
            setInactiveAgentModal({ open: true, onSubmitSuccess: proceed, onSubmitError: close });
          }
        }}
      />
      <LoadingOverlay loading={shouldShowLoader}>
        {skipRendering || isDataPreparing ? null : renderMessage || renderContent}
        {elementsInCollisionModal.state === 'open' && (
          <ElementsInCollisionModal
            agents={composerState.agents}
            isOpen
            onSubmit={async () => {
              dispatch(resolveConflictsOnElements(elementsInCollisionModal.productionElementsInConflict));
              setElementsInCollisionModal({ state: 'resolved' });
              toast.success(t('schedule.modal.collisionWithElementsResolved'));
            }}
            onClose={() => {
              setElementsInCollisionModal({ state: 'loading' });
              setGoToFirstStep(true);
            }}
            productionSchedulesInConflict={elementsInCollisionModal.productionSchedulesInConflict}
            ptoRequestsInConflict={elementsInCollisionModal.ptoRequestsInConflict}
          />
        )}
      </LoadingOverlay>
    </Content>
  );
};

export const SchedulingComposer = memo(
  SchedulingComposerComponent,
  (
    {
      createdScheduleId: previousCreatedScheduleId,
      isLoading: isLoadingPrevious,
      viewType: previousViewType,
      schedule: previousSchedule,
    },
    { createdScheduleId, isLoading, viewType, schedule }
  ) =>
    isLoading === isLoadingPrevious &&
    createdScheduleId === previousCreatedScheduleId &&
    viewType === previousViewType &&
    schedule?.info?.state === previousSchedule?.info?.state &&
    schedule?.info?.lockType === previousSchedule?.info?.lockType
);
